import axios from 'axios'
import type {
  FunnelStatus,
  Insurer,
  Location,
  Practice,
  Psychologist,
  TreatmentProgram,
  TRoleName,
  User
} from '../types'
import { ConsultationPrice, FileType } from '../types'

class Client {
  url = import.meta.env.VITE_APP_API || 'http://localhost:3030'
  token = null

  constructor(url?: string) {
    if (url) {
      this.url = url
    }

    // Redirect to login on 401 (expired/invalid session) - SLAAP-299 inactivity fix
    // But only for authenticated users, not for login attempts or when already on login
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error?.response?.status === 401) {
          const currentPath = window.location.pathname
          const url = error?.config?.url || ''
          const isLoginRequest = url.includes('/login') || url.includes('/send_pin') || url.endsWith('/')
          const isOnLoginPage = currentPath === '/login' || currentPath === '/'
          const hasToken = sessionStorage.getItem('jwt')
          
          // Only redirect if:
          // 1. User was authenticated (has token)
          // 2. Not already on login page  
          // 3. Not a login request
          // This handles session expiration/inactivity but not login failures
          if (hasToken && !isOnLoginPage && !isLoginRequest) {
            try {
              sessionStorage.clear()
              localStorage.clear()
            } catch {}
            console.log('Session expired due to inactivity - redirecting to login')
            window.location.href = '/login'
          }
        }
        return Promise.reject(error)
      }
    )
  }

  getToken() {
    return sessionStorage.getItem('jwt')
  }

  getHeaders() {
    return { Authorization: `Bearer ${this.getToken()}` }
  }

  // NOTE: add more configuration items here
  getConfig() {
    return { headers: this.getHeaders() }
  }

  isLoggedIn() {
    return this.getToken()
  }

  async login(email: string, password: string, pin: string) {
    const response = await axios.post(`${this.url}/login`, {
      email: email,
      password: password,
      pin: pin
    })
    return response.data as {
      email: string
      exp: number
      psychologistId: number
      roles: string[]
      userId: number
    }
  }

  async sendPin(email: string, password: string) {
    // must be an axios request - when using fetch, the body is not received on the backend for some reason
    const response = await axios.put(`${this.url}/send_pin`, {
      email: email,
      password: password
    })
    return response.status === 200
  }

  async isAccessible(): Promise<{
    accessible: boolean
    reason?: string
    ipAddress?: string
  }> {
    try {
      const response = await axios.get(`${this.url}`)
      return { accessible: response.status === 200 }
    } catch (e: any) {
      if (
        e.response?.status === 403 &&
        e.response?.data?.error === 'Access denied'
      ) {
        return {
          accessible: false,
          reason: 'ip_address_blacklisted',
          ipAddress: e.response?.data?.ipAddress
        }
      }
      return { accessible: false, reason: 'other' }
    }
  }

  async saveTreatmentProgram(id: number, program: TreatmentProgram) {
    const response = await axios.put(
      `${this.url}/treatment_programs/${id}`,
      program,
      this.getConfig()
    )
    return response.data
  }

  async updatePatient(id: number, patient: any) {
    const response = await axios.put(
      `${this.url}/patients/${id}`,
      patient,
      this.getConfig()
    )
    return response.data
  }

  async updateInvoice(id: number, invoice: any) {
    const response = await axios.patch(
      `${this.url}/invoices/${id}`,
      invoice,
      this.getConfig()
    )
    return response.data
  }

  async searchPatients(name: string) {
    const url = `${this.url}/patients`
    const response = await axios.get(url, {
      params: {
        search: name
      },
      ...this.getConfig()
    })
    return response.data || [] // return empty array if no data
  }

  async findPatientByEmailOrPhoneNumber({
    email,
    phoneNumber
  }: {
    email?: string
    phoneNumber?: number
  }) {
    const url = `${this.url}/patients`
    const response = await axios.get(url, {
      ...this.getConfig(),
      params: { email, phoneNumber }
    })
    return response.data
  }

  async isBsnTaken(bsn: string, excludeId?: number) {
    const response = await axios.get(`${this.url}/patients/check-bsn`, {
      ...this.getConfig(),
      params: { bsn, excludeId }
    })
    return response.data?.exists === true
  }

  async getPatients(pageSize: number, page: number, practitionerType?: 'primary' | 'secondary', treatmentStatus?: 'active' | 'finished') {
    let url = pageSize !== undefined && page !== undefined
        ? `${this.url}/patients?pageSize=${pageSize}&page=${page}`
        : `${this.url}/patients`;
    
    const params = new URLSearchParams();
    if (pageSize !== undefined && page !== undefined) {
      params.append('pageSize', pageSize.toString());
      params.append('page', page.toString());
    }
    if (practitionerType) {
      params.append('practitionerType', practitionerType);
    }
    if (treatmentStatus) {
      params.append('treatmentStatus', treatmentStatus);
    }
    
    if (params.toString()) {
      url = `${this.url}/patients?${params.toString()}`;
    }
    
    const response = await axios.get(url, this.getConfig())
    return response.data
  }

  async getFunnels(status = null, page = 1, perPage = null) {
    let query = `?page=${page}`
    if (status) query += `&status=${status}`
    if (perPage) query += `&perPage=${perPage}`

    const url = `${this.url}/funnels${query}`
    const response = await axios.get(url, this.getConfig())
    return response.data
  }

  async getFunnelById(funnelId: number) {
    const url = `${this.url}/funnels/${funnelId}`
    const response = await axios.get(url, this.getConfig())
    return response.data
  }

  async removeLastFunnelStatusLogItem(funnelId: number) {
    const url = `${this.url}/funnels/${funnelId}/statusLog/?last`
    const response = await axios.delete(url, this.getConfig())
    return response.data
  }

  async getFunnelsByPatientId(patientId: number) {
    const url = `${this.url}/patients/${patientId}/funnels`
    const response = await axios.get(url, this.getConfig())
    return response.data
  }

  async getPatientCount(practitionerType?: 'primary' | 'secondary', treatmentStatus?: 'active' | 'finished') {
    const params = new URLSearchParams();
    params.append('count', 'true');
    if (practitionerType) {
      params.append('practitionerType', practitionerType);
    }
    if (treatmentStatus) {
      params.append('treatmentStatus', treatmentStatus);
    }
    
    const url = `${this.url}/patients?${params.toString()}`;
    const response = await axios.get(url, this.getConfig())
    return response.data
  }

  // NEW OPTIMIZED ENDPOINTS FOR PRACTITIONER SECTIONS
  async getCaseloadPatients(pageSize = 1000, page = 0) {
    const response = await axios.get(
      `${this.url}/patients/caseload?pageSize=${pageSize}&page=${page}`,
      this.getConfig()
    )
    return response.data
  }

  async getCompletedTreatments(pageSize = 10, page = 0, search?: string) {
    let url = `${this.url}/patients/completed-treatments?pageSize=${pageSize}&page=${page}`
    if (search) {
      url += `&search=${encodeURIComponent(search)}`
    }
    const response = await axios.get(url, this.getConfig())
    return response.data
  }

  async getCoPractitionerPatients(pageSize = 10, page = 0, search?: string) {
    let url = `${this.url}/patients/co-practitioner?pageSize=${pageSize}&page=${page}`
    if (search) {
      url += `&search=${encodeURIComponent(search)}`
    }
    const response = await axios.get(url, this.getConfig())
    return response.data
  }

  async getCompletedCoPractitionerTreatments(pageSize = 10, page = 0, search?: string) {
    let url = `${this.url}/patients/completed-co-practitioner?pageSize=${pageSize}&page=${page}`
    if (search) {
      url += `&search=${encodeURIComponent(search)}`
    }
    const response = await axios.get(url, this.getConfig())
    return response.data
  }

  async getPatientCounts() {
    const response = await axios.get(
      `${this.url}/patients/counts`,
      this.getConfig()
    )
    return response.data
  }

  async getTreatmentProgramById(programId: string): Promise<TreatmentProgram> {
    const response = await axios.get(
      `${this.url}/treatment_programs/${programId}`,
      this.getConfig()
    )

    return response.data as TreatmentProgram
  }

  async getTreatmentProgramsByPatientId(patientId: number) {
    const response = await axios.get(
      `${this.url}/patients/${patientId}/treatment_programs`,
      this.getConfig()
    )

    return response.data
  }

  getOptions(key: string) {
    const cached = sessionStorage.getItem(key)
    return cached ? JSON.parse(cached) : null
  }

  setOptions(key: string, object: any) {
    sessionStorage.setItem(key, JSON.stringify(object))
  }

  clearCacheItem(key: string) {
    sessionStorage.removeItem(key)
  }

  async getCachedOptions(fn: () => Promise<any>, key: string) {
    if (import.meta.env.MODE === 'development') {
      return fn()
    }
    let options = this.getOptions(key)
    if (options) {
      return options
    }
    options = await fn()
    this.setOptions(key, options)
    return options
  }

  async getFunnelStatusOptions(params?: unknown): Promise<Array<FunnelStatus>> {
    const response = await axios.get(`${this.url}/funnel_statuses`, {
      ...this.getConfig(),
      params
    })
    return response.data
  }

  async updateFunnel(funnelId: number, funnel: any) {
    const response = await axios.patch(
      `${this.url}/funnels/${funnelId}`,
      funnel,
      this.getConfig()
    )
    return response.data
  }

  async updateFunnelStatus(funnelId: number, statusId: number) {
    const response = await axios.post(
      `${this.url}/funnels/${funnelId}/statusLog`,
      { id: statusId },
      this.getConfig()
    )
    return response.data
  }

  async updateFunnelStatusByTreatmentProgram(programId: number, statusId: number) {
    const response = await axios.post(
      `${this.url}/treatment_programs/${programId}/funnel/statusLog`,
      { id: statusId },
      this.getConfig()
    )
    return response.data
  }
  async ensureFunnelForProgram(programId: number) {
    const response = await axios.post(
      `${this.url}/treatment_programs/${programId}/ensure_funnel`,
      {},
      this.getConfig()
    )
    return response.data
  }

  async setFunnelTreatmentProgram(funnelId: number, treatmentProgramId: number, statusId: number) {
    const response = await axios.patch(
      `${this.url}/funnels/${funnelId}`,
      {
        treatmentProgram: { id: treatmentProgramId },
        status: { id: statusId }
      },
      this.getConfig()
    )
    return response.data
  }

  async changeOrder(funnelId: number, operation: 'up' | 'down' | 'top' | 'bottom') {
    const response = await axios.patch(
      `${this.url}/funnels/${funnelId}/order`,
      {
        funnelId,
        operation
      },
      this.getConfig()
    )
    return response.data
  }

  async addFunnel(funnel: any) {
    const response = await axios.post(
      `${this.url}/funnels`,
      funnel,
      this.getConfig()
    )
    return response.data
  }

  async getPracticeOptions(primary = false): Promise<Practice[]> {
    return this.getCachedOptions(async () => {
      const response = await axios.get(
        `${this.url}/practices`,
        this.getConfig()
      )

      if (primary) {
        return response.data.filter((el: any) => el.primary === true)
      } else {
        return response.data
      }
    }, `practiceOptions${primary ? 'Primary' : ''}`)
  }

  async updatePractice(
    id: number | string,
    data: Partial<Practice>
  ): Promise<void> {
    await axios.patch(`${this.url}/practices/${id}`, data, this.getConfig())
  }

  async addPracticeLocation(
    practiceId: number,
    locationId: number
  ): Promise<void> {
    await axios.post(
      `${this.url}/practices/${practiceId}/locations`,
      { locationId },
      this.getConfig()
    )
  }

  async removePracticeLocation(
    practiceId: number,
    locationId: number
  ): Promise<void> {
    await axios.delete(
      `${this.url}/practices/${practiceId}/locations/${locationId}`,
      this.getConfig()
    )
  }

  async getLocationOptions(): Promise<Location[]> {
    return this.getCachedOptions(async () => {
      const response = await axios.get(
        `${this.url}/locations`,
        this.getConfig()
      )

      return response.data
    }, 'locationOptions')
  }

  async updateLocation(
    id: number | string,
    data: Partial<Location>
  ): Promise<Location> {
    const res = await axios.patch(
      `${this.url}/locations/${id}`,
      data,
      this.getConfig()
    )
    return res.data
  }

  async createLocation(data: Omit<Location, 'id'>): Promise<Location> {
    const res = await axios.post(
      `${this.url}/locations`,
      data,
      this.getConfig()
    )
    return res.data
  }

  async getSalutationOptions() {
    // TODO: 'Anders' should not be hardcoded
    const getName = ({ salutationShort }: { salutationShort: string }) =>
      salutationShort === '' ? 'Anders' : salutationShort
    return this.getCachedOptions(async () => {
      const response = await axios.get(`${this.url}/genders`, this.getConfig())
      return response.data.map((el: any) => {
        return { id: el.id, name: getName(el) }
      })
    }, 'salutationOptions')
  }

  async getCountryOptions() {
    return this.getCachedOptions(async () => {
      const response = await axios.get(
        `${this.url}/countries`,
        this.getConfig()
      )

      return response.data
    }, 'countryOptions')
  }

  async getInsurerOptions(): Promise<Insurer[]> {
    return this.getCachedOptions(async () => {
      const response = await axios.get(`${this.url}/insurers`, this.getConfig())

      return response.data
    }, 'insurerOptions')
  }

  async getPsychologistOptions(isActive?: boolean): Promise<Psychologist[]> {
    //isActive used to not work before. "Fixing" it broke some parts of the app, so probably better to remove it for now
    return await this.getCachedOptions(
      async () => {
        const response =
          typeof isActive === 'undefined'
            ? await axios.get(`${this.url}/psychologists`, {
                ...this.getConfig()
              })
            : await axios.get(`${this.url}/psychologists`, {
                ...this.getConfig(),
                params: {
                  isActive
                }
              })
        const psychologistOptions = response.data
        const onlyWithName = psychologistOptions.filter(
          (option: any) => option.user?.firstName
        )
        return onlyWithName
      },
      isActive ? `activePsychologistOptions` : 'allPsychologistOptions'
    )
  }

  async getReferrerTypeOptions() {
    return this.getCachedOptions(async () => {
      const response = await axios.get(
        `${this.url}/referrer_types`,
        this.getConfig()
      )

      return response.data
    }, 'referrerTypeOptions')
  }

  async searchReferrers(name: string) {
    const response = await axios.get(
      `${this.url}/referrers?search=${name}`,
      this.getConfig()
    )

    return response.data
  }

  async getProductOptions() {
    return this.getCachedOptions(async () => {
      const response = await axios.get(`${this.url}/products`, this.getConfig())

      return response.data
    }, 'productOptions')
  }

  async getHoNOSTypes() {
    return this.getCachedOptions(async () => {
      const response = await axios.get(
        `${this.url}/ho_nos_type`,
        this.getConfig()
      )

      return response.data
    }, 'hoNOSTypes')
  }

  async getCareDemandTypes() {
    return this.getCachedOptions(async () => {
      const response = await axios.get(
        `${this.url}/care_demand_types`,
        this.getConfig()
      )

      return response.data
    }, 'careDemandTypes')
  }

  async getSortedTreatmentPrograms(patientId: number) {
    const response = await axios.get(
      `${this.url}/patients/${patientId}/treatment_programs`,
      this.getConfig()
    )

    return response.data
  }

  async getActivityOptions(legacy: boolean) {
    return this.getCachedOptions(async () => {
      const response = await axios.get(`${this.url}/activities`, {
        ...this.getConfig(),
        //@ts-ignore //todo
        legacy
      })
      return response.data
    }, 'activityOptions')
  }

  async getActivity(id: number) {
    const response = await axios.get(
      `${this.url}/activities/${id}`,
      this.getConfig()
    )

    return response.data
  }

  async getTreatmentTypeOptions() {
    return this.getCachedOptions(async () => {
      const response = await axios.get(
        `${this.url}/treatment_types`,
        this.getConfig()
      )

      return response.data
    }, 'treatmentTypeOptions')
  }

  async getRoleOptions() {
    const response = await axios.get(`${this.url}/roles`, this.getConfig())

    return response.data
  }

  async getClosingReasonOptions(isLegacy?: boolean) {
    return this.getCachedOptions(async () => {
      const response = await axios.get(`${this.url}/closing_reasons`, {
        ...this.getConfig(),
        // @ts-ignore //todo
        isLegacy
      })

      return response.data
    }, 'closingReasonOptions')
  }

  async getConsultationTypeOptions() {
    return this.getCachedOptions(async () => {
      const response = await axios.get(
        `${this.url}/consultation_types`,
        this.getConfig()
      )

      return response.data
    }, 'consultationTypeOptions')
  }

  async getOccupationTypeOptions() {
    return this.getCachedOptions(async () => {
      const response = await axios.get(
        `${this.url}/occupation_types`,
        this.getConfig()
      )

      return response.data
    }, 'occupationTypeOptions')
  }

  async getSettingTypeOptions() {
    return this.getCachedOptions(async () => {
      const response = await axios.get(
        `${this.url}/setting_types`,
        this.getConfig()
      )

      return response.data
    }, 'settingTypeOptions')
  }

  async getDurationOptions() {
    return this.getCachedOptions(async () => {
      const response = await axios.get(
        `${this.url}/durations`,
        this.getConfig()
      )

      return response.data
    }, 'durationOptions')
  }

  async getConsultationPrices() {
    return this.getCachedOptions(async () => {
      const response = await axios.get(
        `${this.url}/consultation_prices`,
        this.getConfig()
      )

      return response.data
    }, 'consultationPrices' + Math.floor(Date.now() / (1000 * 60))) //temporarily cache for 1 minute
  }

  async getTemplateTypeOptions() {
    return this.getCachedOptions(async () => {
      const response = await axios.get(
        `${this.url}/template_types`,
        this.getConfig()
      )

      return response.data
    }, 'templateTypeOptions')
  }

  async uploadFile(
    file: any,
    {
      programId,
      funnelId,
      documentType
    }:
      | {
          programId: number
          funnelId?: never
          documentType: FileType
        }
      | {
          programId?: never
          funnelId: number
          documentType: FileType
        }
  ) {
    const url = programId
      ? `${this.url}/treatment_programs/${programId}/upload?type=${documentType}`
      : `${this.url}/funnels/${funnelId}/upload?type=${documentType}`

    const formData = new FormData()
    formData.append('file', file)
    const config = {
      headers: {
        'content-type': 'multipart/form-data',
        Authorization: `Bearer ${this.getToken()}`
      }
    }
    return axios.post(url, formData, config)
  }

  /**
   * Using hack described here: https://gist.github.com/javilobo8/097c30a233786be52070986d8cdb1743
   */
  async fetchFile(fileId: number, filename: string) {
    const config = this.getConfig()
    // @ts-ignore //todo
    config.responseType = 'blob' // this is apparently required
    const response = await axios.get(`${this.url}/files/${fileId}`, config)

    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    link.remove() // added this additional to the original Gist
  }

  async addActivity2022(programId: number, primaryPsychologistId: number) {
    const data = { settingType: { id: 1 } }

    if (primaryPsychologistId) {
      // @ts-ignore //todo
      data.psychologist = { id: primaryPsychologistId }
    }

    const response = await axios.post(
      `${this.url}/treatment_programs/${programId}/activities2022`,
      data,
      this.getConfig()
    )

    return response.data
  }

  async addDisorder(disorderId: number, programId: number, isPrimary: boolean, remark: string) {
    const response = await axios.post(
      `${this.url}/treatment_programs/${programId}/disorders`,
      { id: disorderId, isPrimary, remark },
      this.getConfig()
    )

    return response.data
  }

  async addUser(data: any   ) {
    const response = await axios.post(
      `${this.url}/users`,
      data,
      this.getConfig()
    )
    return response.data
  }

  async changePassword(id: number, oldPassword: string, newPassword: string) {
    const response = await axios.put(
      `${this.url}/users/${id}?oldPassword=${oldPassword}`,
      { password: newPassword },
      { ...this.getConfig() }
    )
    return response.data.id
  }

  async changeUserRoles(id: number, roles: TRoleName[]) {
    const response = await axios.post(
      `${this.url}/users/${id}/roles`,
      {
        roles
      },
      { ...this.getConfig() }
    )
    return response.data.id
  }

  async getAllUsers(): Promise<
    {
      id: number
      email: string
      firstName: string
      lastName: string
      isActive: boolean
      title: string
      roles: { id: number; name: string; description: string }[]
    }[]
  > {
    const { data } = await axios.get(`${this.url}/users`, this.getConfig())
    return data
  }

  async removePatient({ id, reason }: { id: number, reason: string }) {
    const response = await axios.delete(`${this.url}/patients/${id}`, {
      ...this.getConfig(),
      params: { reason }
    })

    return response.data
  }

  async removeTreatmentProgram(programId: number) {
    const response = await axios.delete(
      `${this.url}/treatment_programs/${programId}`,
      this.getConfig()
    )

    return response.data
  }

  async addTreatmentProgram(
    treatmentProgram: {
      primaryPsychologist?: { id: number }
      insuranceNumber?: any
      initialRemarks?: any
      patient: {
        id: number
      }
    },
    parentProgramId?: number
  ) {
    const response = await axios.post(
      `${this.url}/treatment_programs`,
      {
        treatmentProgram,
        parentProgramId
      },
      this.getConfig()
    )
    return response.data
  }

  async removeActivity(activityId: number) {
    const response = await axios.delete(
      `${this.url}/registered_activities/${activityId}`,
      this.getConfig()
    )

    return response.data
  }

  async removeActivity2022(activityId: number) {
    const response = await axios.delete(
      `${this.url}/registered_activities2022/${activityId}`,
      this.getConfig()
    )

    return response.data
  }

  async removeDisorder(disorderId: number) {
    const response = await axios.delete(
      `${this.url}/registered_disorders/${disorderId}`,
      this.getConfig()
    )

    return response.data
  }

  async removeFile(fileId: number) {
    const response = await axios.delete(
      `${this.url}/files/${fileId}`,
      this.getConfig()
    )

    return response.data
  }

  async removeFunnel(funnelId: number) {
    const response = await axios.delete(
      `${this.url}/funnels/${funnelId}`,
      this.getConfig()
    )

    return response.data
  }

  async addNote(programId: number) {
    const response = await axios.post(
      `${this.url}/treatment_programs/${programId}/session_notes`,
      {},
      this.getConfig()
    )

    return response.data
  }

  async removeNote(noteId: number | string) {
    const response = await axios.delete(
      `${this.url}/session_notes/${noteId}`,
      this.getConfig()
    )

    return response.data
  }

  async updateNote(noteId: number | string, note: any) {
    const response = await axios.patch(
      `${this.url}/session_notes/${noteId}`,
      note,
      this.getConfig()
    )

    return response.data
  }

  async findFunnelByTreatmentProgramId(treatmentProgramId: number | string) {
    const response = await axios.get(
      `${this.url}/treatment_programs/${treatmentProgramId}/funnel`,
      this.getConfig()
    )

    return response.data
  }

  async findNotesByTreatmentProgramId(treatmentProgramId: number | string) {
    const response = await axios.get(
      `${this.url}/treatment_programs/${treatmentProgramId}/session_notes`,
      this.getConfig()
    )

    return response.data
  }

  async addressLookup(postalCode: string) {
    const response = await axios.get(
      `${this.url}/addressLookup?postalCode=${postalCode}`,
      this.getConfig()
    )
    return response.data
  }

  async findPractitionerByAgb(agb: string) {
    const response = await axios.get(`${this.url}/agb/${agb}`, this.getConfig())
    return response.status === 200 ? response.data : {}
  }

  async checkPasswordStrength(password: string) {
    const response = await axios.post(
      `${this.url}/actions/checkPasswordStrength?password=${password}`,
      undefined,
      this.getConfig()
    )
    return response.data.data.errors
  }

  async addIpAddress(address: string) {
    const response = await axios.post(
      `${this.url}/whitelist`,
      { address },
      this.getConfig()
    )
    return response.data
  }

  async createInvoice(invoice: any) {
    const response = await axios.post(
      `${this.url}/invoices`,
      invoice,
      this.getConfig()
    )
    return response.data
  }

  async deleteInvoice(id: number | string) {
    const response = await axios.delete(
      `${this.url}/invoices/${id}`,
      this.getConfig()
    )
    return response.data
  }

  async getInvoicesByTreatmentProgram(programId: number | string  ) {
    const response = await axios.get(
      `${this.url}/treatment_programs/${programId}/invoices`,
      this.getConfig()
    )
    return response.data
  }

  async getActivities2022ByTreatmentProgram(programId: number | string) {
    const response = await axios.get(
      `${this.url}/treatment_programs/${programId}/activities2022`,
      this.getConfig()
    )
    return response.data
  }

  async getWhitelistedAddresses() {
    const response = await axios.get(`${this.url}/whitelist`, this.getConfig())
    return response.data
  }

  async getTemplate(userId: number | string, practiceId: number | string, fieldName: string) {
    const response = await axios.get(
      `${this.url}/users/${userId}/practices/${practiceId}/templates/${fieldName}`,
      this.getConfig()
    )
    return response.data
  }

  async createTemplate(text: string, userId: number | string, practiceId: number | string, fieldName: string) {
    const url = `${this.url}/users/${userId}/practices/${practiceId}/templates/${fieldName}`
    const response = await axios.put(url, { text }, this.getConfig())
    return response.data
  }

  async getTreatmentsPerMonth() {
    const response = await axios.get(
      `${this.url}/statistics/treatments_per_month`,
      this.getConfig()
    )
    return response.data
  }

  async getTreatmentsPerMonthSplitByTherapist() {
    const response = await axios.get(
      `${this.url}/statistics/treatments_per_month_split_by_therapist`,
      this.getConfig()
    )
    return response.data
  }

  async getRevenuePerMonthSplitByTherapist() {
    const response = await axios.get(
      `${this.url}/statistics/revenue_per_month_split_by_therapist`,
      this.getConfig()
    )
    return response.data
  }

  async getSessionsPerTherapist() {
    const response = await axios.get(
      `${this.url}/statistics/sessions_per_therapist`,
      this.getConfig()
    )
    return response.data
  }

  async completedTreatmentPrograms() {
    const response = await axios.get(
      `${this.url}/statistics/completed_treatment_programs`,
      this.getConfig()
    )
    return response.data
  }

  async caseload2() {
    const response = await axios.get(
      `${this.url}/statistics/caseload2`,
      this.getConfig()
    )
    return response.data
  }

  async caseload() {
    const response = await axios.get(
      `${this.url}/statistics/caseload`,
      this.getConfig()
    )
    return response.data
  }

  async clientRegistrations() {
    const response = await axios.get(
      `${this.url}/statistics/client_registrations`,
      this.getConfig()
    )
    return response.data
  }

  async diagnoses(year: number) {
    const response = await axios.get(`${this.url}/statistics/diagnoses`, {
      ...this.getConfig(),
      params: { year }
    })
    return response.data
  }

  async genders(year: number) {
    const response = await axios.get(`${this.url}/statistics/genders`, {
      ...this.getConfig(),
      params: { year }
    })
    return response.data
  }

  async age() {
    const response = await axios.get(
      `${this.url}/statistics/age`,
      this.getConfig()
    )
    return response.data
  }

  async postcodes() {
    const response = await axios.get(
      `${this.url}/statistics/postcodes`,
      this.getConfig()
    )
    return response.data
  }

  async sessionsPerPractice() {
    const response = await axios.get(
      `${this.url}/statistics/sessions_per_practice`,
      this.getConfig()
    )
    return response.data
  }

  async sessionsPerWeek() {
    const response = await axios.get(
      `${this.url}/statistics/sessions_per_week`,
      this.getConfig()
    )
    return response.data
  }

  async consultationPriceUsage(
    psychologistId?: string | number,
    year?: string | number
  ): Promise<
    Array<{
      price_code: string
      activity_count: number
    }>
  > {
    const response = await axios.get(
      `${this.url}/statistics/consultation_price_usage`,
      { ...this.getConfig(), params: { psychologistId, year } }
    )
    return response.data
  }

  async resetCredentials(email: string): Promise<void> {
    const url = `${this.url}/users/reset_credentials`
    await axios.post(url, { email }, this.getConfig())
  }

  async updateUser(
    userId: number | string,
    data: Partial<User>
  ): Promise<User> {
    const response = await axios.patch(
      `${this.url}/users/${userId}`,
      data,
      this.getConfig()
    )
    return response.data
  }

  async updatePsychologist(
    psychologistId: number | string,
    data: Partial<Psychologist>
  ): Promise<Psychologist> {
    const response = await axios.patch(
      `${this.url}/psychologists/${psychologistId}`,
      data,
      this.getConfig()
    )
    return response.data
  }

  async updateInsurer(
    insurerId: number | string,
    data: Partial<Insurer>
  ): Promise<Insurer> {
    const response = await axios.patch(
      `${this.url}/insurers/${insurerId}`,
      data,
      this.getConfig()
    )
    return response.data
  }

  async getTreatmentPrograms(
    page: number,
    pageSize: number,
    sortField?: string,
    sortDirection?: 'asc' | 'desc',
    treatmentTypeId?: number | null,
    statusId?: number | null,
    practiceId?: number | null,
    psychologistId?: number | null
  ): Promise<{ programs: TreatmentProgram[]; total: number }> {
    const response = await axios.get(
      `${this.url}/treatment_programs?page=${page}&pageSize=${pageSize}${
        sortField ? `&sortField=${JSON.stringify(sortField)}` : ''
      }${sortDirection ? `&sortOrder=${sortDirection.toUpperCase()}` : ''}${
        treatmentTypeId ? `&treatmentTypeId=${treatmentTypeId}` : ''
      }${statusId ? `&statusId=${statusId}` : ''}${
        practiceId ? `&practiceId=${practiceId}` : ''
      }${psychologistId ? `&psychologistId=${psychologistId}` : ''}`,
      this.getConfig()
    )
    return response.data
  }

  async getTimeRegistrationStats(
    psychologistId?: number,
    code?: string,
    year?: number,
    page: number = 1,
    pageSize: number = 10
  ): Promise<{
    data: Array<{
      psychologistId: number
      psychologistName: string
      productCode: number
      count: number
      year: number
    }>
    total: number
    page: number
    pageSize: number
  }> {
    const params = new URLSearchParams()
    if (psychologistId)
      params.append('psychologistId', psychologistId.toString())
    if (code) params.append('code', code)
    if (year) params.append('year', year.toString())
    params.append('page', page.toString())
    params.append('pageSize', pageSize.toString())

    const response = await axios.get(
      `${this.url}/time-registration-stats?${params.toString()}`,
      this.getConfig()
    )
    return response.data
  }

  async getConsultationCodes(): Promise<ConsultationPrice[]> {
    const response = await axios.get(
      `${this.url}/consultation_prices`,
      this.getConfig()
    )
    return response.data
  }

  async getDailyRegisteredActivities(
    psychologistId?: number,
    startDate?: string,
    endDate?: string,
    page: number = 1,
    pageSize: number = 10
  ): Promise<{
    data: Array<{
      id: string
      psychologistId: number
      psychologistName: string
      date: string
      totalDuration: string
      activityCount: string
    }>
    total: number
    page: number
    pageSize: number
  }> {
    const params = new URLSearchParams()
    if (psychologistId)
      params.append('psychologistId', psychologistId.toString())
    if (startDate) params.append('startDate', startDate)
    if (endDate) params.append('endDate', endDate)
    params.append('page', page.toString())
    params.append('pageSize', pageSize.toString())

    const response = await axios.get(
      `${this.url}/activities-per-day?${params.toString()}`,
      this.getConfig()
    )
    return response.data
  }
  async getRegisteredActivities(
    psychologistId?: number,
    startDate?: string,
    endDate?: string,
    page: number = 1,
    pageSize: number = 10
  ): Promise<{
    data: Array<{
      id: string
      psychologistId: number
      psychologistName: string
      date: string
      startTime: string
      totalDuration: string
      consultationType: string
    }>
    total: number
    page: number
    pageSize: number
  }> {
    const params = new URLSearchParams()
    if (psychologistId)
      params.append('psychologistId', psychologistId.toString())
    if (startDate) params.append('startDate', startDate)
    if (endDate) params.append('endDate', endDate)
    params.append('page', page.toString())
    params.append('pageSize', pageSize.toString())

    const response = await axios.get(
      `${this.url}/statistics/activities?${params.toString()}`,
      this.getConfig()
    )
    return response.data
  }

  async getAvgRegisteredActivities(
    psychologistId?: number,
    startDate?: string,
    endDate?: string,
    page: number = 1,
    pageSize: number = 10
  ): Promise<{
    data: Array<{
      psychologistId: number
      psychologistName: string
      totalDays: number
      totalDuration: number
      avgDurationPerDay: number
    }>
    total: number
    page: number
    pageSize: number
  }> {
    const params = new URLSearchParams()
    if (psychologistId)
      params.append('psychologistId', psychologistId.toString())
    if (startDate) params.append('startDate', startDate)
    if (endDate) params.append('endDate', endDate)
    params.append('page', page.toString())
    params.append('pageSize', pageSize.toString())

    const response = await axios.get(
      `${this.url}/avg-activities-per-day?${params.toString()}`,
      this.getConfig()
    )
    return response.data
  }

  // ---------- Dossier-cleanup admin tool ----------
  async getDossierCleanupSummary() {
    const response = await axios.get(
      `${this.url}/admin/dossier-cleanup/summary`,
      this.getConfig()
    )
    return response.data as {
      markedPatientCards: number
      markedFiles: number
      softDeletedTotal: number
    }
  }

  async listDossierCleanup(params: {
    state?: 'marked' | 'soft_deleted' | 'all'
    reason?: 'patient_card_60d' | 'file_90d'
    search?: string
    page?: number
    pageSize?: number
  }) {
    const qs = new URLSearchParams()
    if (params.state) qs.set('state', params.state)
    if (params.reason) qs.set('reason', params.reason)
    if (params.search) qs.set('search', params.search)
    if (params.page) qs.set('page', String(params.page))
    if (params.pageSize) qs.set('pageSize', String(params.pageSize))

    const response = await axios.get(
      `${this.url}/admin/dossier-cleanup?${qs.toString()}`,
      this.getConfig()
    )
    return response.data as {
      data: DossierCleanupRow[]
      total: number
      page: number
      pageSize: number
    }
  }

  async listDossierCleanupIds(params: {
    state?: 'marked' | 'soft_deleted' | 'all'
    reason?: 'patient_card_60d' | 'file_90d'
    search?: string
  }) {
    const qs = new URLSearchParams()
    if (params.state) qs.set('state', params.state)
    if (params.reason) qs.set('reason', params.reason)
    if (params.search) qs.set('search', params.search)

    const response = await axios.get(
      `${this.url}/admin/dossier-cleanup/ids?${qs.toString()}`,
      this.getConfig()
    )
    return response.data as {
      funnelIds: number[]
      truncated: boolean
    }
  }

  async restoreDossierCleanup(funnelId: number) {
    const response = await axios.post(
      `${this.url}/admin/dossier-cleanup/${funnelId}/restore`,
      {},
      this.getConfig()
    )
    return response.data
  }

  async softDeleteDossierCleanup(funnelId: number) {
    const response = await axios.post(
      `${this.url}/admin/dossier-cleanup/${funnelId}/soft-delete`,
      {},
      this.getConfig()
    )
    return response.data
  }

  async runDossierCleanup() {
    const response = await axios.post(
      `${this.url}/admin/dossier-cleanup/run`,
      {},
      this.getConfig()
    )
    return response.data as {
      status: 'success'
      data: {
        patientCardsMarked: number
        filesMarked: number
        totalScanned: number
        startedAt: string
        finishedAt: string
      }
    }
  }

  async resetAllDossierCleanup() {
    const response = await axios.post(
      `${this.url}/admin/dossier-cleanup/reset-all`,
      {},
      this.getConfig()
    )
    return response.data as {
      status: 'success'
      data: { affected: number }
    }
  }

  async bulkRestoreDossierCleanup(funnelIds: number[]) {
    const response = await axios.post(
      `${this.url}/admin/dossier-cleanup/bulk/restore`,
      { funnelIds },
      this.getConfig()
    )
    return response.data as {
      status: 'success'
      data: { affected: number }
    }
  }

  async bulkSoftDeleteDossierCleanup(funnelIds: number[]) {
    const response = await axios.post(
      `${this.url}/admin/dossier-cleanup/bulk/soft-delete`,
      { funnelIds },
      this.getConfig()
    )
    return response.data as {
      status: 'success'
      data: { affected: number }
    }
  }

  async undoSoftDeleteDossierCleanup(funnelId: number) {
    const response = await axios.post(
      `${this.url}/admin/dossier-cleanup/${funnelId}/undo-soft-delete`,
      undefined,
      this.getConfig()
    )
    return response.data as { status: 'success' }
  }

  async bulkUndoSoftDeleteDossierCleanup(funnelIds: number[]) {
    const response = await axios.post(
      `${this.url}/admin/dossier-cleanup/bulk/undo-soft-delete`,
      { funnelIds },
      this.getConfig()
    )
    return response.data as {
      status: 'success'
      data: { affected: number }
    }
  }
}

export interface DossierCleanupRow {
  funnelId: number
  patientId: number
  patientFirstName: string | null
  patientInfix: string | null
  patientLastName: string | null
  patientEmail: string | null
  patientPhoneNumber: string | null
  treatmentProgramId: number | null
  currentStatusId: number | null
  currentStatusName: string | null
  currentStatusChangedAt: string | null
  markedForDeletionAt: string
  markedForDeletionReason: 'patient_card_60d' | 'file_90d'
  deletionLastActivityAt: string | null
  softDeletedAt: string | null
  softDeletedByEmail: string | null
  practiceName: string | null
  primaryPsychologistName: string | null
}

/**
 * NOTE: we know how to handle UnauthorizedExceptions (redirect to login page)
 * so we want to centralize its handling and rethrow all other exceptions.
 *
 * A class decorator could easily wrap each method with this functionality, but
 * it requires eject the create-react-app. Therefore we implement a poor man's
 * alternative to decorators.
 *
 * An instance of class is instantiated. We then iterate of its methods and wrap
 * it with an exception check, redirecting or rethrowing.
 */

const _client = new Client()

const clientWrapper: Record<string, any> = {}

// eslint-disable-next-line no-unused-vars
for (const key of Object.getOwnPropertyNames(Client.prototype)) {
  const login = () => {
    window.location.href = '/login'
  }

  clientWrapper[key] = async (...args: any) => {
    try {
      if (
        _client.isLoggedIn() ||
        ['login', 'isAccessible', 'sendPin'].includes(key)
      ) {
        return (_client as any)[key](...args) // call the wrapped method
      }
      // All methods except login(), sendPin() and isAccessible() require authentication
      return login()
    } catch (e: any) {
      console.error(e)
      if (
        e?.response?.status === 401 &&
        !['login', 'isAccessible', 'sendPin'].includes(key)
      ) {
        login()
      }
    }
  }
}

const inferedClientWrapper = clientWrapper as unknown as Client
export { inferedClientWrapper as client }
